import { FLOOR_PRICE_RULE_ADDRESS, KIOSK_LOCK_RULE_ADDRESS, PERSONAL_KIOSK_RULE_ADDRESS, ROYALTY_RULE_ADDRESS, getBaseRules, rules } from "../constants.mjs";
import { fetchKiosk, fetchKioskExtension, getOwnedKiosks } from "../query/kiosk.mjs";
import { queryOwnedTransferPolicies, queryTransferPolicy, queryTransferPolicyCapsByType } from "../query/transfer-policy.mjs";
//#region src/client/kiosk-client.ts
/**
* Creates a kiosk client extension that can be used with `client.$extend()`.
*
* @example
* ```ts
* import { SafeGrpcClient } from '@safecoin/safe/grpc';
* import { kiosk } from '@safecoin/kiosk';
*
* const client = new SafeGrpcClient({
*   baseUrl: 'https://fullnode.mainnet.safecoin.org:443',
*   network: 'mainnet',
* }).$extend(kiosk());
*
* const ownedKiosks = await client.kiosk.getOwnedKiosks({ address: '0x...' });
* ```
*/
function kiosk({ name = "kiosk", packageIds } = {}) {
	return {
		name,
		register: (client) => {
			return new KioskClient({
				client,
				network: client.network,
				packageIds
			});
		}
	};
}
/**
* A Client that allows you to interact with kiosk.
* Offers utilities to query kiosk, craft transactions to edit your own kiosk,
* purchase, manage transfer policies, create new kiosks etc.
* If you pass packageIds, all functionality will be managed using these packages.
*/
var KioskClient = class {
	constructor(options) {
		this.client = options.client;
		this.network = options.network;
		this.rules = rules;
		this.packageIds = options.packageIds;
		if (options.packageIds) this.rules.push(...getBaseRules(options.packageIds));
	}
	/**
	* Get an addresses's owned kiosks.
	* @param address The address for which we want to retrieve the kiosks.
	* @param pagination Optional pagination arguments.
	* @returns An Object containing all the `kioskOwnerCap` objects as well as the kioskIds.
	*/
	async getOwnedKiosks({ address, pagination }) {
		const personalPackageId = this.packageIds?.personalKioskRulePackageId || PERSONAL_KIOSK_RULE_ADDRESS[this.network];
		return getOwnedKiosks(this.client, address, {
			pagination,
			personalKioskType: personalPackageId ? `${personalPackageId}::personal_kiosk::PersonalKioskCap` : ""
		});
	}
	/**
	* Fetches the kiosk contents.
	* @param id The ID of the kiosk to fetch.
	* @param options Optional to control the fetch behavior.
	* @returns
	*/
	async getKiosk({ id, options }) {
		return (await fetchKiosk(this.client, id, {}, options || {})).data;
	}
	/**
	* Fetch the extension data (if any) for a kiosk, by type
	* @param kioskId The ID of the kiosk to lookup
	* @param extensionType The Type of the extension (can be used from by using the type returned by `getKiosk()`)
	*/
	async getKioskExtension({ kioskId, type }) {
		return fetchKioskExtension(this.client, kioskId, type);
	}
	/**
	* Query the Transfer Policy(ies) for type `T`.
	* @param type The Type we're querying for (E.g `0xMyAddress::hero::Hero`)
	*/
	async getTransferPolicies({ type }) {
		return queryTransferPolicy(this.client, type);
	}
	/**
	* Query all the owned transfer policies for an address.
	* Returns `TransferPolicyCap` which uncludes `policyId, policyCapId, type`.
	* @param address The address we're searching the owned transfer policies for.
	*/
	async getOwnedTransferPolicies({ address }) {
		return queryOwnedTransferPolicies(this.client, address);
	}
	/**
	* Query the Transfer Policy Cap for type `T`, owned by `address`
	* @param type The Type `T` for the object
	* @param address The address that owns the cap.
	*/
	async getOwnedTransferPoliciesByType({ type, address }) {
		return queryTransferPolicyCapsByType(this.client, address, type);
	}
	addRuleResolver(rule) {
		if (this.rules.find((x) => x.rule === rule.rule)) throw new Error(`Rule ${rule.rule} resolver already exists.`);
		this.rules.push(rule);
	}
	/**
	* A convenient helper to get the packageIds for our supported ruleset,
	* based on `kioskClient` configuration.
	*/
	getRulePackageId(rule) {
		const rules = this.packageIds || {};
		const network = this.network;
		if (!rules[rule] && network !== "mainnet" && network !== "testnet") throw new Error(`Missing packageId for rule ${rule}`);
		switch (rule) {
			case "kioskLockRulePackageId": return rules[rule] || KIOSK_LOCK_RULE_ADDRESS[network];
			case "royaltyRulePackageId": return rules[rule] || ROYALTY_RULE_ADDRESS[network];
			case "personalKioskRulePackageId": return rules[rule] || PERSONAL_KIOSK_RULE_ADDRESS[network];
			case "floorPriceRulePackageId": return rules[rule] || FLOOR_PRICE_RULE_ADDRESS[network];
		}
	}
};
//#endregion
export { KioskClient, kiosk };

//# sourceMappingURL=kiosk-client.mjs.map